/**
 * Vendor Locations Hook
 * 
 * Manages vendor plant/facility locations for bid submission.
 * Allows vendors to register multiple manufacturing locations
 * which are used for per-item tax origin calculations.
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface VendorLocation {
  id: string;
  vendor_user_id: string;
  location_name: string | null;
  address_text: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  latitude: number | null;
  longitude: number | null;
  is_primary: boolean;
  address_validated: boolean | null;
  address_validated_at: string | null;
  address_deliverability: string | null;
  created_at: string;
  updated_at: string;
}

export interface VendorLocationInput {
  location_name: string;
  address_text: string;
  city: string;
  state: string;
  country: string;
  postal_code: string;
  is_primary?: boolean;
  address_validated?: boolean;
  address_validated_at?: string;
  address_deliverability?: string;
}

export const useVendorLocations = (userId?: string) => {
  const [locations, setLocations] = useState<VendorLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const fetchLocations = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('vendor_locations')
        .select('*')
        .eq('vendor_user_id', userId)
        .order('is_primary', { ascending: false })
        .order('location_name', { ascending: true });

      if (fetchError) throw fetchError;
      setLocations((data as VendorLocation[]) || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch locations';
      setError(message);
      console.error('Error fetching vendor locations:', err);
    } finally {
      setLoading(false);
    }
  };

  const createLocation = async (location: VendorLocationInput) => {
    if (!userId) throw new Error('User not authenticated');

    try {
      setLoading(true);
      setError(null);

      // If this is the first location or marked as primary, handle primary flag
      if (location.is_primary) {
        // Unset any existing primary
        await supabase
          .from('vendor_locations')
          .update({ is_primary: false } as any)
          .eq('vendor_user_id', userId);
      }

      const { error: insertError } = await supabase
        .from('vendor_locations')
        .insert({
          vendor_user_id: userId,
          location_name: location.location_name,
          address_text: location.address_text,
          city: location.city,
          state: location.state,
          country: location.country || 'US',
          postal_code: location.postal_code,
          is_primary: location.is_primary ?? false,
          address_validated: location.address_validated ?? false,
          address_validated_at: location.address_validated_at ?? null,
          address_deliverability: location.address_deliverability ?? null,
        } as any);

      if (insertError) throw insertError;

      await fetchLocations();
      
      toast({
        title: 'Success',
        description: 'Location added successfully',
      });

      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create location';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = async (locationId: string, updates: Partial<VendorLocationInput>) => {
    if (!userId) throw new Error('User not authenticated');

    try {
      setLoading(true);

      // If setting as primary, unset others first
      if (updates.is_primary) {
        await supabase
          .from('vendor_locations')
          .update({ is_primary: false } as any)
          .eq('vendor_user_id', userId);
      }

      const { error: updateError } = await supabase
        .from('vendor_locations')
        .update(updates as any)
        .eq('id', locationId);

      if (updateError) throw updateError;

      await fetchLocations();
      
      toast({
        title: 'Success',
        description: 'Location updated successfully',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update location';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteLocation = async (locationId: string) => {
    try {
      setLoading(true);

      const location = locations.find(l => l.id === locationId);
      if (location?.is_primary && locations.length > 1) {
        toast({
          title: 'Cannot delete',
          description: 'Set another location as primary before deleting this one',
          variant: 'destructive',
        });
        return;
      }

      const { error: deleteError } = await supabase
        .from('vendor_locations')
        .delete()
        .eq('id', locationId);

      if (deleteError) throw deleteError;

      await fetchLocations();
      
      toast({
        title: 'Success',
        description: 'Location deleted successfully',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete location';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const setPrimary = async (locationId: string) => {
    await updateLocation(locationId, { is_primary: true });
  };

  useEffect(() => {
    if (userId) {
      fetchLocations();
    }
  }, [userId]);

  const primaryLocation = locations.find(l => l.is_primary) || locations[0];

  return {
    locations,
    primaryLocation,
    loading,
    error,
    fetchLocations,
    createLocation,
    updateLocation,
    deleteLocation,
    setPrimary,
  };
};
